import React, { useEffect, useRef, useState } from 'react';
import { Overlay } from 'react-bootstrap';
import useOnClickOutside from 'src/hooks/useOnClickOutside';
import { useRouter } from 'next/router';
import { AppRoutes } from 'src/lib/routes';
import Link from 'next/link';
import ImageWithFallback from 'src/components/ImageWithFallback';
import Illu11Image from 'public/images/illu/illu_11.png';
import Illu9Image from 'public/images/illu/illu_9.png';
import { FiChevronDown } from 'react-icons/fi';
import classNames from 'classnames';
import useBreakpoints from 'src/hooks/useBreakpoints';
import ClientOnlyPortal from 'src/components/ClientOnlyPortal';
import useFilter from 'src/hooks/useFilter';
import useInitialData from 'src/hooks/useInitialData';
import { sortBy } from 'lodash-es';
import { useTranslations } from 'next-intl';

export const GiftItems: React.FC = () => {
  const translation = useTranslations('components.GiftDropdown')
  const initialData = useInitialData();
  const giftGuideNavs = initialData.settings.giftGuideNavs;

  return (
    <>
      <div className="tw-mb-8">
        <div className="tw-font-semibold tw-font-styleSans tw-text-2xl tw-text-brand-navy tw-mb-3 tw-leading-none">
          {translation('gifts')}
        </div>
        <Link legacyBehavior prefetch={false} href={AppRoutes.giftsSendRoute().toPath()}>
          <a className="tw-block tw-mb-2 tw-cursor-pointer tw-text-brand-navy hover:tw-font-semibold">
            {translation('giftCardEver')}
          </a>
        </Link>
        <Link legacyBehavior prefetch={false} href={AppRoutes.giftsRoute().toPath()}>
          <a className="tw-block tw-mb-2 tw-cursor-pointer tw-text-brand-navy hover:tw-font-semibold">
            {translation('allGiftExperience')}
          </a>
        </Link>
      </div>
      {giftGuideNavs && giftGuideNavs.length > 0 ? (
        <div>
          <div className="tw-font-semibold tw-font-styleSans tw-text-2xl tw-text-brand-navy tw-mb-3 tw-leading-none">
            {translation('giftGuide')}
          </div>
          {giftGuideNavs.map((o, index) => (
            <Link legacyBehavior prefetch={false} href={AppRoutes.shortMagazineRoute({ id: o.blogId }).toPath()} key={index}>
              <a className="tw-block tw-mb-2 tw-cursor-pointer tw-text-brand-navy hover:tw-font-semibold">
                {o.title}
              </a>
            </Link>
          ))}
        </div>
      ) : null}
    </>
  );
};

type Props = {};

const GiftDropdown: React.FC<Props> = () => {
  const translation = useTranslations('components.GiftDropdown')
  const { isScreenMd } = useBreakpoints();
  const [show, setShow] = useState(false);
  const buttonRef = useRef(null);
  const eleRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const initialData = useInitialData();
  const singapore = initialData.countries.find((o) => o.slug === 'singapore');
  const giftGuideNavs = initialData.settings.giftGuideNavs;
  const giftCollections = initialData.giftCollections;

  const { setShouldCloseFilterItem } = useFilter();

  useOnClickOutside(eleRef, (eventTarget) => {
    if (overlayRef.current?.contains(eventTarget)) {
      return;
    }
    setShow(false);
  });

  useEffect(() => {
    if (router.isReady) {
      router.events.on('routeChangeComplete', () => {
        setShow(false);
      });
    }
  }, [router]);

  return (
    <>
      <div ref={eleRef}>
        <div
          className="tw-cursor-pointer"
          ref={buttonRef}
          onClick={() => setShow(true)}
          onMouseEnter={() => {
            setShow(true);
            setShouldCloseFilterItem((prev) => prev + 1);
          }}
          onMouseLeave={() => setShow(false)}
        >
          <span>{translation('gifts')}</span>
          <FiChevronDown size={16} />
        </div>

        <Overlay
          popperConfig={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -5],
                },
              },
            ],
          }}
          placement="bottom"
          target={buttonRef.current}
          show={show}
        >
          {({ placement, arrowProps, show: _show, popper, ...props }) => (
            <div
              {...props}
              ref={(e) => {
                props.ref(e);
                overlayRef.current = e;
              }}
              style={{
                ...props.style,
                left: '-5px',
                width: '100%',
              }}
              className="tw-pt-7 tw-z-1200"
              onMouseEnter={() => setShow(true)}
              onMouseLeave={() => setShow(false)}
            >
              <div className="tw-bg-brand-yellow tw-shadow-top tw-py-8 tw-font-styleSans">
                <div className="container tw-px-8">
                  <div className="tw-flex tw-justify-center">
                    <div className="tw-pl-16 tw-text-center">
                      <Link legacyBehavior prefetch={false} href={AppRoutes.giftsSendRoute().toPath()}>
                        <div className="tw-cursor-pointer">
                          <div className="tw-text-xl tw-font-styleSans tw-font-semibold">
                            {translation('giftCardEver')}
                          </div>
                          <ImageWithFallback
                            src={Illu9Image}
                            alt=""
                            className="tw-w-auto tw-h-32 tw-inline-block tw-static"
                          />
                        </div>
                      </Link>
                    </div>
                    <div className="tw-px-16 tw-text-center">
                      <Link legacyBehavior prefetch={false} href={AppRoutes.giftsRoute().toPath()}>
                        <div className="tw-cursor-pointer">
                          <div className="tw-text-xl tw-font-styleSans tw-font-semibold">
                            {translation('giftExperience')}
                          </div>
                          <ImageWithFallback
                            src={Illu11Image}
                            alt=""
                            className="tw-w-auto tw-h-32 tw-inline-block tw-static"
                          />
                        </div>
                      </Link>
                    </div>
                    <div className="tw-border-l tw-border-solid tw-h tw-border-grey-lighter tw-mx-14"></div>
                    <div className="tw-pr-12">
                      <div className="tw-font-styleSans tw-font-semibold tw-mb-2">{translation('allGift')}</div>
                      <Link legacyBehavior prefetch={false} href={AppRoutes.giftsSendRoute().toPath()}>
                        <a className="tw-block tw-mb-2 tw-font-styleSans tw-cursor-pointer tw-text-brand-navy hover:tw-underline tw-break-words">
                          {translation('giftCardEver')}
                        </a>
                      </Link>
                      {giftCollections && giftCollections.length > 0
                        ? sortBy(giftCollections, (o) =>
                            o.giftCountryId === singapore?.id ? 0 : 1
                          ).map((o, index) => (
                            <Link legacyBehavior prefetch={false} href={AppRoutes.collectionRoute({ id: o.slugCode || o.id }).toPath()} key={index}>
                              <a className="tw-block tw-mb-2 tw-font-styleSans tw-cursor-pointer tw-text-brand-navy hover:tw-underline tw-break-words">
                                {o.name}
                              </a>
                            </Link>
                          ))
                        : null}
                    </div>
                    {giftGuideNavs && giftGuideNavs.length > 0 ? (
                      <div>
                        <div className="tw-font-styleSans tw-font-semibold tw-mb-2">{translation('giftGuide')}</div>
                        {giftGuideNavs.map((o, index) => (
                          <Link legacyBehavior prefetch={false}                           href={AppRoutes.shortMagazineRoute({ id: o.blogId }).toPath()}
                            key={index}
                          >
                            <a className="tw-block tw-mb-2 tw-font-styleSans tw-cursor-pointer tw-text-brand-navy hover:tw-underline tw-break-words">
                              {o.title}
                            </a>
                          </Link>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Overlay>
      </div>

      <ClientOnlyPortal selector="#bottom-body">
        {isScreenMd ? (
          <div
            className={classNames(
              'tw-bg-black tw-bottom-0 tw-left-0 tw-opacity-30 tw-fixed tw-w-screen tw-z-101 dropdown-mask-height',
              { 'tw-hidden': !show }
            )}
          ></div>
        ) : null}
      </ClientOnlyPortal>
    </>
  );
};

export default GiftDropdown;
